import { mapValue, stringValue } from "@/utils/coerce";

/**
 * 👍 / 👎 on one message, read from the message itself.
 *
 * `conversation_message.metadata.feedback` is a dict keyed by member email,
 * each entry `{ is_positive, submitted_at, categories, comment }` — the only
 * place 👍 is stored at all (base_agent `upsert_feedback`). The admin messages
 * endpoint returns `metadata` unscrubbed of it, so the thread can tag every
 * answer without a second request.
 */

export type FeedbackEntry = {
  email: string;
  comment: string;
  categories: string[];
  submittedAt: string;
};

export type MessageFeedback = {
  down: FeedbackEntry[];
  up: FeedbackEntry[];
};

export function messageFeedback(message: unknown): MessageFeedback {
  const byEmail = mapValue(mapValue(mapValue(message).metadata).feedback);
  const out: MessageFeedback = { down: [], up: [] };
  for (const [email, raw] of Object.entries(byEmail)) {
    const entry = mapValue(raw);
    if (typeof entry.is_positive !== "boolean") continue;
    const item: FeedbackEntry = {
      email,
      comment: stringValue(entry.comment, "").trim(),
      categories: Array.isArray(entry.categories)
        ? entry.categories.filter((c): c is string => typeof c === "string")
        : [],
      submittedAt: stringValue(entry.submitted_at, ""),
    };
    (entry.is_positive ? out.up : out.down).push(item);
  }
  // Stable across loads — object key order is insertion order on the server.
  const byTime = (a: FeedbackEntry, b: FeedbackEntry) =>
    a.submittedAt.localeCompare(b.submittedAt) || a.email.localeCompare(b.email);
  out.down.sort(byTime);
  out.up.sort(byTime);
  return out;
}
